import { capitalize } from "@common/helpers/string";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import {
    DescriptionList,
    DescriptionListItem,
} from "@design-system/DescriptionList/DescriptionList";
import { Drawer } from "@design-system/Drawer";
import { HStack } from "@design-system/HStack/HStack";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { formatDateOnly } from "@helpers/date";
import type { DemoEntity, DemoPerson, DemoTask } from "./fixtures/demoTasks";
import { getTaskTypeLabel } from "./taskBoardConfig";
import { TaskStatusPill } from "@components/tasks/board/TaskStatusPill";
import styles from "./BoardPlayground.module.scss";

interface TaskDetailsDrawerProps {
    task: DemoTask | undefined;
    isOpen: boolean;
    stageTitle: string;
    assignee: DemoPerson | undefined;
    entities: readonly DemoEntity[];
    onClose: () => void;
}

const EMPTY_VALUE = "-";

/** Read-only drawer with the clicked task's fields; keeps them while closing. */
export const TaskDetailsDrawer = ({
    task,
    isOpen,
    stageTitle,
    assignee,
    entities,
    onClose,
}: Readonly<TaskDetailsDrawerProps>) => (
    <Drawer
        open={isOpen}
        onClose={onClose}
        size="quarter"
        headerAddonLeft={<h2 className={styles.drawerTitle}>{task?.title}</h2>}
    >
        {task !== undefined && (
            <DescriptionList>
                <DescriptionListItem label="Status">
                    <TaskStatusPill status={task.status} />
                </DescriptionListItem>
                <DescriptionListItem label="Stage">
                    {stageTitle}
                </DescriptionListItem>
                <DescriptionListItem label="Assignee">
                    {assignee === undefined ? (
                        EMPTY_VALUE
                    ) : (
                        <HStack gap={6} align="center">
                            <UserAvatar user={assignee} aria-hidden />
                            {assignee.name}
                        </HStack>
                    )}
                </DescriptionListItem>
                <DescriptionListItem label="Entities" alignTop>
                    <ul className={styles.drawerList}>
                        {entities.map((entity) => (
                            <HStack
                                as="li"
                                key={entity.id}
                                gap={6}
                                align="center"
                            >
                                <EntityAvatar entity={entity} aria-hidden />
                                {entity.name}
                            </HStack>
                        ))}
                    </ul>
                </DescriptionListItem>
                <DescriptionListItem label="Due date">
                    {formatDateOnly(task.dueDateOnly)}
                </DescriptionListItem>
                <DescriptionListItem label="Priority">
                    {task.priority === null
                        ? EMPTY_VALUE
                        : capitalize(task.priority)}
                </DescriptionListItem>
                <DescriptionListItem label="Type">
                    {getTaskTypeLabel(task.type)}
                </DescriptionListItem>
                <DescriptionListItem label="Estimate">
                    {task.estimateMinutes === null
                        ? EMPTY_VALUE
                        : formatDuration(task.estimateMinutes)}
                </DescriptionListItem>
                <DescriptionListItem label="Comments">
                    {task.commentCount}
                </DescriptionListItem>
            </DescriptionList>
        )}
    </Drawer>
);
